import type { ReactNode } from 'react';

/**
 * 단추 (19회차, 콘솔 검토 A-4(c)·D-1).
 *
 * 16회차까지 보조 단추 클래스가 12곳에 복사돼 있었고 포커스 링은 어디에도 없었다.
 * 링은 `index.css`의 전역 `:focus-visible`이 준다. 여기서는 모양과 색만 정한다.
 * 모양은 18회차까지의 각진 작은 단추다(19회차 보완).
 *
 * - `primary`   파랑 채움. 화면마다 하나만 둔다
 * - `secondary` 테두리만. 새로고침·초기화처럼 되돌릴 수 있는 것
 * - `link`      글자만. 표 안의 이동처럼 여러 개가 늘어서는 자리
 *
 * `size="sm"`은 표 안이나 구획 머리에 놓는 작은 단추다.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'link';
export type ButtonSize = 'md' | 'sm';

const BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium ' +
  'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'rounded-lg bg-accent text-white hover:bg-accent-hover',
  secondary: 'rounded-lg border border-line bg-panel text-muted hover:bg-band hover:text-ink',
  link: 'rounded-md text-link hover:bg-band-strong',
};

const SIZE: Record<ButtonSize, string> = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-1.5 text-xs',
};

/** 글자 단추는 크기와 상관없이 작다 */
function classOf(variant: ButtonVariant, size: ButtonSize): string {
  const sizing = variant === 'link' ? 'px-2 py-1 text-xs font-normal' : SIZE[size];
  return `${BASE} ${VARIANT[variant]} ${sizing}`;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  onClick,
  disabled,
  title,
  children,
  type = 'button',
  ariaLabel,
  ariaExpanded,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
  type?: 'button' | 'submit';
  ariaLabel?: string;
  /** 펼치고 접는 단추면 지금 펼쳐졌는지. 스크린리더가 글자만으로 알 수 없어 따로 알린다 (44회차) */
  ariaExpanded?: boolean;
}) {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      className={classOf(variant, size)}
    >
      {children}
    </button>
  );
}

/**
 * 단추처럼 보이는 링크. 주소가 있는 이동은 `<a>`여야 새 탭으로 열 수 있고
 * 스크린리더도 "링크"로 읽는다 (13회차에 표 첫 열에서 쓰던 방식과 같다).
 */
export function LinkButton({
  href,
  variant = 'secondary',
  size = 'md',
  download,
  title,
  children,
}: {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  download?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      download={download}
      title={title}
      className={`${classOf(variant, size)} no-underline`}
    >
      {children}
    </a>
  );
}
